import React, { useState, useEffect } from 'react';
import { Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MirageUserSelector } from '@/components/MirageUserSelector';
import { usersApi, type UserData } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

interface EditUserDialogProps {
  user: UserData;
  onUserUpdated: () => void;
}

export function EditUserDialog({ user, onUserUpdated }: EditUserDialogProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    full_name: user.full_name || '',
    email: user.email || '',
    mirage_user_id: user.mirage_user_id || null,
    mirage_username: user.mirage_username || null,
    role: user.role || 'regular',
  });

  // Reset form data when dialog opens or user changes
  useEffect(() => {
    if (open) {
      setFormData({
        full_name: user.full_name || '',
        email: user.email || '',
        mirage_user_id: user.mirage_user_id || null,
        mirage_username: user.mirage_username || null,
        role: user.role || 'regular',
      });
    }
  }, [open, user]);

  const handleMirageUserChange = (mirageUserId: number | null, mirageUsername: string | null) => {
    setFormData((prev) => ({
      ...prev,
      mirage_user_id: mirageUserId,
      mirage_username: mirageUsername,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await usersApi.updateUser(user.id, {
        full_name: formData.full_name,
        email: formData.email,
        mirage_user_id: formData.mirage_user_id,
        mirage_username: formData.mirage_username,
      });

      if (formData.role !== user.role) {
        await usersApi.updateUserRole(user.id, formData.role);
      }

      toast({
        title: 'Success',
        description: `User ${formData.full_name} updated successfully${
          formData.mirage_user_id
            ? ' and linked to accounting software user'
            : ''
        }`,
      });

      onUserUpdated();
      setOpen(false);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update user',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" title="Edit User">
          <Edit2 className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>
            Update user information and link to accounting software user for proper audit trail in the accounting system.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="full_name">Full Name</Label>
            <Input
              id="full_name"
              value={formData.full_name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, full_name: e.target.value }))
              }
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, email: e.target.value }))
              }
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="role">Role</Label>
            <Select
              value={formData.role}
              onValueChange={(value) => setFormData((prev) => ({ ...prev, role: value }))}
            >
              <SelectTrigger id="role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="regular">Regular</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <MirageUserSelector
            selectedUserId={formData.mirage_user_id}
            onSelect={handleMirageUserChange}
            disabled={loading}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

